/**
 * Utilidades para normalizar y validar números de teléfono
 */

/**
 * Normaliza un número de teléfono al formato E.164 (+1XXXXXXXXXX)
 * 
 * @param phone Número de teléfono en cualquier formato
 * @param defaultCountryCode Código de país por defecto (ej: '+1' para RD/US)
 * @returns Número normalizado en formato E.164 o null si no es válido
 * 
 * @example
 * normalizeToE164('8095551234') => '+18095551234'
 * normalizeToE164('+1 809 555 1234') => '+18095551234'
 * normalizeToE164('(809) 555-1234') => '+18095551234'
 */
export function normalizeToE164(phone: string | null | undefined, defaultCountryCode: string = '+1'): string | null {
  if (!phone) return null;

  // Limpiar caracteres especiales, espacios y guiones
  let cleaned = phone.replace(/[\s\-\(\)\.]/g, '');

  // Si ya tiene formato E.164, retornarlo
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // Si empieza con '1' y tiene 11 dígitos, agregar '+'
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return '+' + cleaned;
  }

  // Si tiene 10 dígitos, agregar código de país por defecto
  if (/^\d{10}$/.test(cleaned)) {
    return defaultCountryCode + cleaned;
  }

  // Si tiene más de 10 dígitos sin código de país, intentar agregarlo
  if (/^\d{11,15}$/.test(cleaned)) {
    return '+' + cleaned;
  }

  return null;
}

/**
 * Valida si un número está en formato E.164
 * 
 * @param phone Número de teléfono
 * @returns true si está en formato E.164 válido
 * 
 * @example
 * isValidE164('+18095551234') => true
 * isValidE164('8095551234') => false
 */
export function isValidE164(phone: string | null | undefined): boolean {
  if (!phone) return false;
  return /^\+[1-9]\d{1,14}$/.test(phone);
}

/**
 * Detecta si un string es un email o un teléfono
 * 
 * @param input String a analizar
 * @returns 'email', 'phone', o null
 * 
 * @example
 * detectInputType('user@example.com') => 'email'
 * detectInputType('8095551234') => 'phone'
 * detectInputType('+18095551234') => 'phone'
 */
export function detectInputType(input: string | null | undefined): 'email' | 'phone' | null {
  if (!input) return null;

  const trimmed = input.trim();

  // Si contiene @, es email
  if (trimmed.includes('@')) {
    return 'email';
  }

  // Si contiene solo dígitos, espacios, guiones, paréntesis o +, es teléfono
  if (/^[\d\s\-\+\(\)]+$/.test(trimmed)) {
    return 'phone';
  }

  return null;
}

/**
 * Formatea un número E.164 para visualización
 * 
 * @param phone Número en formato E.164
 * @returns Número formateado para visualización
 * 
 * @example
 * formatPhoneDisplay('+18095551234') => '(809) 555-1234'
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';

  const cleaned = phone.replace(/[^\d]/g, '');

  // Si es número de RD/US (10 dígitos después del +1)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const areaCode = cleaned.slice(1, 4);
    const first = cleaned.slice(4, 7);
    const second = cleaned.slice(7);
    return `(${areaCode}) ${first}-${second}`;
  }

  // Si tiene 10 dígitos
  if (cleaned.length === 10) {
    const areaCode = cleaned.slice(0, 3);
    const first = cleaned.slice(3, 6);
    const second = cleaned.slice(6);
    return `(${areaCode}) ${first}-${second}`;
  }

  // Retornar como está si no coincide con formato conocido
  return phone;
}
